import React, { useCallback, useRef } from "react";
import "altcha";
import type { AltchaWidgetElement } from "altcha/dist/types/generic";
import { apiBaseUrl } from "api/api";

/**
 * ALTCHA proof-of-work captcha.
 *
 * Self-hosted: the challenge comes from our own backend and the work happens in
 * the visitor's browser. No third-party request, no cookie, nothing to consent
 * to — which is why it replaced reCAPTCHA rather than being repaired alongside
 * it.
 *
 * `auto="off"` is deliberate. The widget's "onsubmit" mode hooks the form's
 * native submit event, but every form here submits through a React handler that
 * posts via securePost, so the handler would read the hidden input before the
 * widget had finished solving. Instead the caller awaits `solve()` immediately
 * before the request. That also yields a fresh payload per request, which
 * matters because the server now enforces single use — a replayed solution is
 * rejected.
 */
export const CAPTCHA_CHALLENGE_URL = `${apiBaseUrl}/api/captcha/challenge`;

export type AltchaHandle = {
  /** Renders the widget. Mount this inside the form. */
  widget: React.ReactElement;
  /**
   * Solves a fresh challenge and returns the payload to put in the request.
   * Returns null if solving fails; callers should treat null as "do not submit".
   */
  solve: () => Promise<string | null>;
};

export const useAltcha = (name = "altcha"): AltchaHandle => {
  const ref = useRef<AltchaWidgetElement | null>(null);

  const solve = useCallback(async (): Promise<string | null> => {
    const element = ref.current;
    if (!element) return null;

    try {
      // Discard any previous solution so each request carries its own.
      element.reset();
      const result = await element.verify();
      return result?.payload ?? null;
    } catch {
      return null;
    }
  }, []);

  const widget = (
    <altcha-widget
      ref={ref}
      challenge={CAPTCHA_CHALLENGE_URL}
      name={name}
      auto="off"
      display="standard"
    />
  );

  return { widget, solve };
};

/**
 * Honeypot input. Invisible to people, tempting to bots that fill every field.
 * The backend rejects any request where `contactReason` is non-empty.
 *
 * Hidden with off-screen positioning rather than `display: none`, since some
 * bots skip fields that are display:none. aria-hidden and tabIndex keep it away
 * from screen readers and keyboard users.
 */
export const Honeypot = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
    <label htmlFor="contactReason">Leave this field empty</label>
    <input
      id="contactReason"
      name="contactReason"
      type="text"
      tabIndex={-1}
      autoComplete="off"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
